import { usePerms } from 'contexts/PermsContext';
import ActionBuilder from 'features/GlobalActions/features/ActionBuilder/index.web';
import ActionList from 'features/GlobalActions/features/ActionList/index.web';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';

export default function GlobalActions() {
  const [showGlobalAction, setShowGlobalAction] = useState(false);
  const [initialData, setInitialData] = useState();
  const [readOnly, setReadOnly] = useState(false);
  const [alarmShareContext, setAlarmShareContext] = useState(null);
  const perms = usePerms();
  const navigate = useNavigate();
  const location = useLocation();
  const consumedPrefillKeyRef = useRef(null);

  const getFallbackReturnPath = useCallback((scope) => {
    return scope === 'history' ? '/overwatch/alarmhistory' : '/overwatch/pendingalarms';
  }, []);

  const exitAlarmShareFlow = useCallback(() => {
    const returnPath =
      alarmShareContext?.returnPath || getFallbackReturnPath(alarmShareContext?.scope);
    navigate(returnPath);
  }, [alarmShareContext, getFallbackReturnPath, navigate]);

  useEffect(() => {
    if (!perms.validatePermissions(['ViewGlobalActions'])) {
      navigate('/404');
      return;
    }
  }, [perms, navigate]);

  useEffect(() => {
    if (!showGlobalAction) setReadOnly(false);
  }, [showGlobalAction]);

  useEffect(() => {
    const prefill = location?.state?.globalActionPrefill;
    if (!prefill) return;
    const contextFromState = location?.state?.alarmShareContext ?? null;

    if (!perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditGlobalActions'])) {
      return;
    }

    const prefillKey = `${prefill?.requestedAt ?? ''}:${prefill?.title ?? ''}`;
    if (consumedPrefillKeyRef.current === prefillKey) return;
    consumedPrefillKeyRef.current = prefillKey;

    setReadOnly(false);
    setInitialData(prefill);
    setShowGlobalAction(true);
    setAlarmShareContext(
      contextFromState ||
        (prefill?.alarmSharePrefill
          ? {
              scope: prefill?.scope || 'pending',
              sourceLabel: prefill?.scope === 'history' ? 'Alarm History' : 'Pending Alarms',
              returnPath: getFallbackReturnPath(prefill?.scope),
            }
          : null),
    );

    const nextState = { ...(location.state || {}) };
    delete nextState.globalActionPrefill;

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: Object.keys(nextState).length > 0 ? nextState : null,
    });
  }, [getFallbackReturnPath, location.pathname, location.search, location.state, navigate, perms]);

  return (
    <div className="global-actions-page h-100 d-flex flex-column min-h-0">
      {!showGlobalAction && (
        <>
          {/* Scrollable list area */}
          {perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewGlobalActions']) && (
            <div className="flex-grow-1 min-h-0 d-flex flex-column p-2">
              <ActionList
                setInitialData={setInitialData}
                setShowGlobalAction={setShowGlobalAction}
                setReadOnly={setReadOnly}
              />
            </div>
          )}

          {/* Footer button area */}
          {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditGlobalActions']) && (
            <div className="flex-shrink-0 d-flex justify-content-end p-2">
              <Button
                variant="dark"
                className="fw-bold px-4 py-2 rounded-3"
                style={{
                  backgroundColor: 'var(--color-primary-highlight)',
                  color: 'var(--color-primary-highlight-foreground)',
                }}
                onClick={() => setShowGlobalAction(true)}
              >
                Create Global Action
              </Button>
            </div>
          )}
        </>
      )}

      {showGlobalAction &&
        (readOnly
          ? perms.testPermission([8, 9, 10, 11, 12, 13], ['ViewGlobalActions'])
          : perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditGlobalActions'])) && (
          <>
            {initialData?.alarmSharePrefill && alarmShareContext?.sourceLabel && (
              <div className="px-3 pt-2 small fw-semibold">
                Sharing alarm from {alarmShareContext.sourceLabel}
              </div>
            )}
            <ActionBuilder
              initialData={initialData}
              setInitialData={setInitialData}
              setShowGlobalAction={setShowGlobalAction}
              readOnly={readOnly}
              onAlarmShareSuccess={initialData?.alarmSharePrefill ? exitAlarmShareFlow : null}
              onAlarmShareCancel={initialData?.alarmSharePrefill ? exitAlarmShareFlow : null}
            />
          </>
        )}
    </div>
  );
}
